import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  DollarSign, 
  Plus, 
  Edit, 
  Trash2, 
  Save,
  Globe,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

function PricingPage() {
  const { t, isRTL } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Default pricing state
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [priceUpto20, setPriceUpto20] = useState(5);
  const [priceUpto50, setPriceUpto50] = useState(4);
  const [priceAbove50, setPriceAbove50] = useState(3);
  
  // Country pricing state
  const [countryPricing, setCountryPricing] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState(null);
  
  // Form state for adding/editing country pricing
  const [formCountryName, setFormCountryName] = useState('');
  const [formCurrency, setFormCurrency] = useState('');
  const [formPriceUpto20, setFormPriceUpto20] = useState(5);
  const [formPriceUpto50, setFormPriceUpto50] = useState(4);
  const [formPriceAbove50, setFormPriceAbove50] = useState(3);
  const [formError, setFormError] = useState('');
  
  useEffect(() => {
    fetchPricing();
  }, []);
  
  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/pricing`, {
        withCredentials: true
      });
      
      const { default: defaultPricing, countries } = response.data;
      
      if (defaultPricing) {
        setDefaultCurrency(defaultPricing.currency || 'USD');
        setPriceUpto20(defaultPricing.price_upto_20 || 5);
        setPriceUpto50(defaultPricing.price_upto_50 || 4);
        setPriceAbove50(defaultPricing.price_above_50 || 3);
      }
      
      setCountryPricing(countries || []);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };
  
  const handleSaveDefaultPricing = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/superadmin/pricing/default`,
        {
          currency: defaultCurrency,
          price_upto_20: priceUpto20,
          price_upto_50: priceUpto50,
          price_above_50: priceAbove50
        },
        { withCredentials: true }
      );
      showMessage('success', 'Default pricing saved successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save default pricing');
    } finally {
      setSaving(false);
    }
  };
  
  const resetForm = () => {
    setFormCountryName('');
    setFormCurrency('');
    setFormPriceUpto20(5);
    setFormPriceUpto50(4);
    setFormPriceAbove50(3);
    setFormError('');
  };
  
  const handleAddCountryPricing = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    
    try {
      const response = await axios.post(
        `${API}/superadmin/pricing/country`,
        {
          country_name: formCountryName,
          currency: formCurrency,
          price_upto_20: formPriceUpto20,
          price_upto_50: formPriceUpto50,
          price_above_50: formPriceAbove50
        },
        { withCredentials: true }
      );
      
      setCountryPricing([...countryPricing, response.data]);
      setAddDialogOpen(false);
      resetForm();
      showMessage('success', 'Country pricing added successfully');
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to add country pricing');
    } finally {
      setSaving(false);
    }
  };
  
  const openEditDialog = (pricing) => {
    setSelectedPricing(pricing);
    setFormCountryName(pricing.country_name);
    setFormCurrency(pricing.currency);
    setFormPriceUpto20(pricing.price_upto_20);
    setFormPriceUpto50(pricing.price_upto_50);
    setFormPriceAbove50(pricing.price_above_50);
    setFormError('');
    setEditDialogOpen(true);
  };
  
  const handleEditCountryPricing = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    
    try {
      const response = await axios.put(
        `${API}/superadmin/pricing/country/${selectedPricing.pricing_id}`,
        {
          country_name: formCountryName,
          currency: formCurrency,
          price_upto_20: formPriceUpto20,
          price_upto_50: formPriceUpto50,
          price_above_50: formPriceAbove50
        },
        { withCredentials: true }
      );
      
      setCountryPricing(countryPricing.map(p => 
        p.pricing_id === selectedPricing.pricing_id ? response.data : p
      ));
      setEditDialogOpen(false);
      setSelectedPricing(null);
      resetForm();
      showMessage('success', 'Country pricing updated successfully');
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to update country pricing');
    } finally {
      setSaving(false);
    }
  };
  
  const openDeleteDialog = (pricing) => {
    setSelectedPricing(pricing);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteCountryPricing = async () => {
    setSaving(true);
    try {
      await axios.delete(
        `${API}/superadmin/pricing/country/${selectedPricing.pricing_id}`,
        { withCredentials: true }
      );
      
      setCountryPricing(countryPricing.filter(p => p.pricing_id !== selectedPricing.pricing_id));
      setDeleteDialogOpen(false);
      setSelectedPricing(null);
      showMessage('success', 'Country pricing deleted successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to delete country pricing');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="pricing-page">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Pricing Management</h2>
        <p className="text-gray-600 mt-1">Configure pricing plans for admin subscriptions</p>
      </div>
      
      {/* Status Message */}
      {message.text && (
        <div className={`
          flex items-center gap-2 p-4 rounded-lg
          ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : ''}
          ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : ''}
        `}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}
      
      {/* Default Pricing Card */}
      <Card className="shadow-lg" data-testid="default-pricing-card">
        <CardHeader className="border-b bg-gray-50">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-indigo-600" />
            Default Pricing
          </CardTitle>
          <CardDescription>Set the default pricing for all regions</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label htmlFor="default-currency">Default Currency</Label>
              <Input
                id="default-currency"
                value={defaultCurrency}
                onChange={(e) => setDefaultCurrency(e.target.value)}
                placeholder="USD"
                data-testid="default-currency-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="price-upto-20">Price (up to 20 staff)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="price-upto-20"
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceUpto20}
                  onChange={(e) => setPriceUpto20(parseFloat(e.target.value) || 0)}
                  className="pl-9"
                  data-testid="price-upto-20-input"
                />
              </div>
              <p className="text-xs text-gray-500">Per staff per month</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="price-upto-50">Price (up to 50 staff)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="price-upto-50"
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceUpto50}
                  onChange={(e) => setPriceUpto50(parseFloat(e.target.value) || 0)}
                  className="pl-9"
                  data-testid="price-upto-50-input"
                />
              </div>
              <p className="text-xs text-gray-500">Per staff per month</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="price-above-50">Price (50+ staff)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="price-above-50"
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceAbove50}
                  onChange={(e) => setPriceAbove50(parseFloat(e.target.value) || 0)}
                  className="pl-9"
                  data-testid="price-above-50-input"
                />
              </div>
              <p className="text-xs text-gray-500">Per staff per month</p>
            </div>
          </div>
          <div className="mt-6">
            <Button 
              onClick={handleSaveDefaultPricing} 
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-default-pricing-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Default Pricing'}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Country-Specific Pricing */}
      <Card className="shadow-lg" data-testid="country-pricing-card">
        <CardHeader className="border-b bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                Country-Specific Pricing
              </CardTitle>
              <CardDescription>Set custom pricing for specific countries</CardDescription>
            </div>
            <Button 
              onClick={() => { resetForm(); setAddDialogOpen(true); }}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="add-country-pricing-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Country
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Country Name</TableHead>
                <TableHead className="font-semibold">Currency</TableHead>
                <TableHead className="font-semibold text-center">Up to 20 Staff</TableHead>
                <TableHead className="font-semibold text-center">Up to 50 Staff</TableHead>
                <TableHead className="font-semibold text-center">50+ Staff</TableHead>
                <TableHead className="text-right font-semibold">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {countryPricing.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                    No country-specific pricing configured. Click "Add Country" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                countryPricing.map((pricing) => (
                  <TableRow key={pricing.pricing_id} data-testid={`pricing-row-${pricing.pricing_id}`}>
                    <TableCell className="font-medium">{pricing.country_name}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                        {pricing.currency}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-green-600">
                        {pricing.currency} {pricing.price_upto_20}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-blue-600">
                        {pricing.currency} {pricing.price_upto_50}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-purple-600">
                        {pricing.currency} {pricing.price_above_50}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(pricing)}
                          className="text-gray-600 hover:text-gray-700"
                          data-testid={`edit-pricing-${pricing.pricing_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(pricing)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-pricing-${pricing.pricing_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Add Country Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Country Pricing</DialogTitle>
            <DialogDescription>Configure pricing for a specific country</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCountryPricing} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="form-country-name">Country Name</Label>
                <Input
                  id="form-country-name"
                  value={formCountryName}
                  onChange={(e) => setFormCountryName(e.target.value)}
                  placeholder="e.g., India"
                  required
                  data-testid="form-country-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="form-currency">Currency</Label>
                <Input
                  id="form-currency"
                  value={formCurrency}
                  onChange={(e) => setFormCurrency(e.target.value)}
                  placeholder="e.g., INR"
                  required
                  data-testid="form-currency-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="form-price-20">Up to 20 Staff</Label>
                <Input
                  id="form-price-20"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formPriceUpto20}
                  onChange={(e) => setFormPriceUpto20(parseFloat(e.target.value) || 0)}
                  data-testid="form-price-20-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="form-price-50">Up to 50 Staff</Label>
                <Input
                  id="form-price-50"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formPriceUpto50}
                  onChange={(e) => setFormPriceUpto50(parseFloat(e.target.value) || 0)}
                  data-testid="form-price-50-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="form-price-above-50">50+ Staff</Label>
                <Input
                  id="form-price-above-50"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formPriceAbove50}
                  onChange={(e) => setFormPriceAbove50(parseFloat(e.target.value) || 0)}
                  data-testid="form-price-above-50-input"
                />
              </div>
            </div>
            {formError && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="form-error">
                {formError}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                {saving ? 'Adding...' : 'Add Country'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit Country Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Country Pricing</DialogTitle>
            <DialogDescription>Update pricing for {selectedPricing?.country_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditCountryPricing} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-country-name">Country Name</Label>
                <Input
                  id="edit-country-name"
                  value={formCountryName}
                  onChange={(e) => setFormCountryName(e.target.value)}
                  required
                  data-testid="edit-country-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-currency">Currency</Label>
                <Input
                  id="edit-currency"
                  value={formCurrency}
                  onChange={(e) => setFormCurrency(e.target.value)}
                  required
                  data-testid="edit-currency-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-price-20">Up to 20 Staff</Label>
                <Input
                  id="edit-price-20"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formPriceUpto20}
                  onChange={(e) => setFormPriceUpto20(parseFloat(e.target.value) || 0)}
                  data-testid="edit-price-20-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-price-50">Up to 50 Staff</Label>
                <Input
                  id="edit-price-50"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formPriceUpto50}
                  onChange={(e) => setFormPriceUpto50(parseFloat(e.target.value) || 0)}
                  data-testid="edit-price-50-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-price-above-50">50+ Staff</Label>
                <Input
                  id="edit-price-above-50"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formPriceAbove50}
                  onChange={(e) => setFormPriceAbove50(parseFloat(e.target.value) || 0)}
                  data-testid="edit-price-above-50-input"
                />
              </div>
            </div>
            {formError && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="edit-form-error">
                {formError}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Country Pricing?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the pricing for <strong>{selectedPricing?.country_name}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCountryPricing}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm"
            >
              {saving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default PricingPage;
